<template>
  <div
    class="ticket-detail-container"
    aria-label="Détail du ticket de support logistique"
    role="main"
  >
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <button
            class="back-btn"
            @click="$router.back()"
          >
            <img
              src="@/assets/images/back.svg"
              alt="Retour"
              width="24"
            >
          </button>
          <div class="header-icon">
            <feather-icon
              icon="FileTextIcon"
              size="24"
            />
          </div>
          <div>
            <div class="title-row">
              <h1 class="page-title">
                Détail du ticket
              </h1>
              <span
                v-if="ticket"
                v-b-tooltip.hover
                class="ref-badge"
                title="Copier la référence"
                @click="copyReference(ticket.reference)"
              >
                {{ ticket.reference }}
                <span
                  v-if="copied"
                  class="ml-1"
                >✔</span>
              </span>
            </div>
            <p class="page-subtitle">
              Consultez et gérez les informations du ticket
            </p>
          </div>
        </div>
        <div
          v-if="ticket"
          class="header-right"
        >
          <span :class="['status-badge-header', 'status-' + getStatusClass(ticket.status)]">
            {{ ticket.status.display_name || ticket.status.name }}
          </span>
        </div>
      </div>
    </div>

    <!-- Action Bar -->
    <div
      v-if="ticket"
      class="action-bar"
    >
      <div class="action-bar-content">
        <div class="ticket-meta">
          <span class="meta-item">
            <feather-icon
              icon="CalendarIcon"
              size="16"
            />
            Créé le {{ formatDate(ticket.created_at) }}
          </span>
          <span class="meta-item">
            <feather-icon
              icon="ClockIcon"
              size="16"
            />
            Mis à jour le {{ formatDate(ticket.updated_at) }}
          </span>
          <span
            v-if="ticket.closed_at"
            class="meta-item closed"
          >
            <feather-icon
              icon="LockIcon"
              size="16"
            />
            Clôturé le {{ formatDate(ticket.closed_at) }}
          </span>
        </div>
        <div class="action-buttons">
          <button
            v-if="canEditTicket && !isClosed"
            class="action-btn edit-btn"
            @click="goToEdit"
          >
            <feather-icon
              icon="EditIcon"
              size="16"
              class="mr-1"
            />
            Modifier
          </button>
          <button
            v-if="!isClosed && canChangeStatus"
            class="action-btn status-btn"
            @click="showStatusModal = true"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-1"
            />
            Changer le statut
          </button>
          <button
            v-if="isAdmin && !isClosed"
            class="action-btn assign-btn"
            @click="showAssignModal = true"
          >
            <feather-icon
              icon="UserPlusIcon"
              size="16"
              class="mr-1"
            />
            Assigner
          </button>
          <button
            v-if="isAdmin"
            class="action-btn delete-btn"
            @click="confirmDeleteTicket"
          >
            <feather-icon
              icon="TrashIcon"
              size="16"
              class="mr-1"
            />
            Supprimer
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div
      v-if="ticket"
      class="main-content"
    >
      <div class="content-grid">
        <!-- Left Column -->
        <div class="left-column">
          <!-- Ticket Info Card -->
          <div class="info-card">
            <div class="card-header-custom">
              <feather-icon
                icon="InfoIcon"
                size="18"
                class="header-icon-small"
              />
              Informations du ticket
            </div>
            <div class="card-body-custom">
              <div class="info-row">
                <span class="info-label">Titre</span>
                <span class="info-value">{{ ticket.title }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Catégorie</span>
                <div>
                  <span class="category-badge">
                    {{ ticket.category ? (ticket.category.display_name || ticket.category.name) : '-' }}
                  </span>
                  <p
                    v-if="ticket.category && ticket.category.description"
                    class="category-desc"
                  >
                    {{ ticket.category.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Description Card -->
          <div class="info-card">
            <div class="card-header-custom">
              <feather-icon
                icon="AlignLeftIcon"
                size="18"
                class="header-icon-small"
              />
              Description
            </div>
            <div class="card-body-custom">
              <!-- eslint-disable vue/no-v-html -->
              <div
                v-if="ticket && ticket.description"
                class="description-content"
                v-html="sanitizedDescription"
              />
              <!-- eslint-enable vue/no-v-html -->
              <div
                v-else
                class="empty-state small"
              >
                <feather-icon
                  icon="FileIcon"
                  size="24"
                  class="empty-icon"
                />
                <span>Aucune description</span>
              </div>
            </div>
          </div>

          <!-- Orders Card -->
          <div class="info-card">
            <div class="card-header-custom">
              <feather-icon
                icon="PackageIcon"
                size="18"
                class="header-icon-small"
              />
              Commandes liées
              <span
                v-if="ticket.orders && ticket.orders.length"
                class="count-badge"
              >
                {{ ticket.orders.length }}
              </span>
            </div>
            <div class="card-body-custom">
              <div
                v-if="ticket.orders && ticket.orders.length"
                class="orders-list"
              >
                <div
                  v-for="order in ticket.orders"
                  :key="order.id"
                  class="order-item"
                >
                  <div class="order-topline">
                    <div class="order-main">
                      <span class="order-id-badge">#{{ order.id }}</span>
                      <div class="order-info">
                        <span class="order-name">{{ order.name || '-' }}</span>
                        <span class="order-phone">{{ formatPhones(order) }}</span>
                      </div>
                    </div>
                    <div class="order-details">
                      <span class="order-price">{{ formatMoney(order.price_ttc) }}</span>
                      <span class="order-status-badge">{{ getOrderStatusLabel(order.status) }}</span>
                    </div>
                  </div>

                  <div class="order-info-grid">
                    <div class="order-info-block wide">
                      <span class="order-info-label">Adresse client</span>
                      <span class="order-info-value pre-line">{{ order.formatted_address || order.address || '-' }}</span>
                    </div>
                    <div
                      v-if="canViewSupplierOrderInfo"
                      class="order-info-block"
                    >
                      <span class="order-info-label">Fournisseur</span>
                      <span class="order-info-value">{{ order.supplier_display_name || '-' }}</span>
                    </div>
                    <div
                      v-if="canViewSupplierOrderInfo"
                      class="order-info-block"
                    >
                      <span class="order-info-label">Téléphone fournisseur</span>
                      <span class="order-info-value">{{ order.supplier_display_phone || '-' }}</span>
                    </div>
                    <div class="order-info-block">
                      <span class="order-info-label">Dépôt</span>
                      <span class="order-info-value">{{ order.repository ? order.repository.name : '-' }}</span>
                    </div>
                    <div class="order-info-block">
                      <span class="order-info-label">Date</span>
                      <span class="order-info-value">{{ formatShortDate(order.date || order.created_at) }}</span>
                    </div>
                    <div class="order-info-block">
                      <span class="order-info-label">Livraison</span>
                      <span class="order-info-value">{{ formatMoney(order.price_delivery) }}</span>
                    </div>
                    <div class="order-info-block">
                      <span class="order-info-label">Retour</span>
                      <span class="order-info-value">{{ order.return_number || 0 }}</span>
                    </div>
                  </div>

                  <div
                    v-if="order.is_an_exchange || order.is_fragile || order.is_discharge || order.can_open_package === false"
                    class="order-flags"
                  >
                    <span
                      v-if="order.is_an_exchange"
                      class="order-flag"
                    >
                      Échange<span v-if="order.exchange_article">: {{ order.exchange_article }}</span>
                    </span>
                    <span
                      v-if="order.is_fragile"
                      class="order-flag"
                    >Fragile</span>
                    <span
                      v-if="order.is_discharge"
                      class="order-flag"
                    >Décharge</span>
                    <span
                      v-if="order.can_open_package === false"
                      class="order-flag"
                    >Ouverture interdite</span>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="empty-state"
              >
                <feather-icon
                  icon="PackageIcon"
                  size="32"
                  class="empty-icon"
                />
                <span>Aucune commande liée</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column -->
        <div class="right-column">
          <!-- People Card -->
          <div class="info-card">
            <div class="card-header-custom">
              <feather-icon
                icon="UsersIcon"
                size="18"
                class="header-icon-small"
              />
              Personnes concernées
            </div>
            <div class="card-body-custom">
              <div class="person-row">
                <span class="person-label">Demandeur</span>
                <div class="person-info">
                  <b-avatar
                    :src="getAvatar(ticket.submitter)"
                    size="40"
                    class="person-avatar"
                  />
                  <div class="person-details">
                    <span class="person-name">{{ ticket.submitter ? ticket.submitter.username : '-' }}</span>
                    <span
                      v-if="ticket.submitter && ticket.submitter.phone"
                      class="person-phone"
                    >{{ ticket.submitter.phone }}</span>
                  </div>
                </div>
              </div>
              <div class="person-row">
                <span class="person-label">Assigné à</span>
                <div class="person-info">
                  <b-avatar
                    :src="getAvatar(ticket.assignee)"
                    size="40"
                    class="person-avatar"
                  />
                  <div class="person-details">
                    <span class="person-name">{{ ticket.assignee ? ticket.assignee.username : 'Non assigné' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Comments Card -->
          <div class="info-card">
            <div class="card-header-custom">
              <feather-icon
                icon="MessageSquareIcon"
                size="18"
                class="header-icon-small"
              />
              Commentaires
              <span
                v-if="ticket.comments && ticket.comments.length"
                class="count-badge"
              >
                {{ ticket.comments.length }}
              </span>
            </div>
            <div class="card-body-custom">
              <div
                v-if="ticket.comments && ticket.comments.length"
                class="comments-list"
              >
                <div
                  v-for="comment in ticket.comments"
                  :key="comment.id"
                  class="comment-item"
                >
                  <b-avatar
                    :src="getAvatar(comment.author)"
                    size="32"
                    class="comment-avatar"
                  />
                  <div class="comment-content">
                    <div class="comment-header">
                      <span class="comment-author">{{ comment.author.username }}</span>
                      <span class="comment-date">{{ formatDate(comment.created_at) }}</span>
                    </div>
                    <p class="comment-text">
                      {{ comment.comment }}
                    </p>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="empty-state small"
              >
                <feather-icon
                  icon="MessageSquareIcon"
                  size="24"
                  class="empty-icon"
                />
                <span>Aucun commentaire</span>
              </div>

              <!-- Add Comment Form -->
              <div
                v-if="canComment && !isClosed"
                class="add-comment-form"
              >
                <b-form @submit.prevent="addComment">
                  <b-form-textarea
                    v-model="commentText"
                    class="comment-input"
                    rows="2"
                    :disabled="commentLoading"
                    placeholder="Ajouter un commentaire..."
                  />
                  <button
                    type="submit"
                    class="add-comment-btn"
                    :disabled="commentLoading || !commentText.trim()"
                  >
                    <feather-icon
                      icon="SendIcon"
                      size="16"
                      class="mr-1"
                    />
                    Envoyer
                  </button>
                  <div
                    v-if="commentError"
                    class="comment-error"
                  >
                    {{ commentError }}
                  </div>
                </b-form>
              </div>
            </div>
          </div>

          <!-- History Card -->
          <div class="info-card">
            <div class="card-header-custom">
              <feather-icon
                icon="ClockIcon"
                size="18"
                class="header-icon-small"
              />
              Historique
            </div>
            <div class="card-body-custom">
              <div
                v-if="ticket.histories && ticket.histories.length"
                class="timeline"
              >
                <div
                  v-for="h in ticket.histories"
                  :key="h.id"
                  class="timeline-item"
                >
                  <span class="timeline-dot" />
                  <div class="timeline-content">
                    <div class="timeline-message">
                      {{ h.message }}
                    </div>
                    <div class="timeline-meta">
                      {{ formatDate(h.created_at) }} - {{ h.description }}
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="empty-state small"
              >
                <feather-icon
                  icon="ClockIcon"
                  size="24"
                  class="empty-icon"
                />
                <span>Aucun historique</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="!ticket"
      class="loading-state"
    >
      <b-spinner
        variant="warning"
        label="Chargement..."
        style="width: 3rem; height: 3rem;"
      />
      <div>Chargement du ticket...</div>
    </div>

    <!-- Status Change Modal -->
    <b-modal
      v-model="showStatusModal"
      title="Changer le statut"
      hide-footer
      centered
    >
      <div class="modal-form">
        <b-form-group label="Nouveau statut">
          <b-form-select
            v-model="selectedStatus"
            :options="filteredStatusList"
            :disabled="statusLoading || isClosed"
            placeholder="Sélectionnez un statut"
            class="custom-select-modal"
          />
        </b-form-group>
        <button
          class="modal-confirm-btn"
          :disabled="statusLoading || !selectedStatus || isClosed"
          @click="changeStatusModal"
        >
          <feather-icon
            icon="CheckIcon"
            size="18"
            class="mr-1"
          />
          Confirmer
        </button>
      </div>
    </b-modal>

    <!-- Assign Modal -->
    <b-modal
      v-model="showAssignModal"
      title="Assigner à un support"
      hide-footer
      centered
    >
      <div class="modal-form">
        <b-form-group label="Support">
          <b-form-select
            v-model="selectedAssignee"
            :options="staffList"
            :disabled="assignLoading || isClosed"
            placeholder="Sélectionnez un staff"
            class="custom-select-modal"
          />
        </b-form-group>
        <button
          class="modal-confirm-btn"
          :disabled="assignLoading || !selectedAssignee || isClosed"
          @click="assignTicketModal"
        >
          <feather-icon
            icon="CheckIcon"
            size="18"
            class="mr-1"
          />
          Confirmer
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BAvatar, BSpinner, VBTooltip, BFormSelect, BFormTextarea, BFormGroup, BForm, BModal,
} from 'bootstrap-vue'
import defaultAvatar from '@/assets/images/avatars/user.png'
import storeAuth from '@/store/store'
import DOMPurify from 'dompurify'

export default {
  directives: { 'b-tooltip': VBTooltip },
  components: {
    BAvatar, BSpinner, BFormSelect, BFormTextarea, BFormGroup, BForm, BModal,
  },
  data() {
    return {
      ticket: null,
      copied: false,
      user: storeAuth.state.user,
      staffList: [],
      statusList: [],
      assignLoading: false,
      statusLoading: false,
      selectedAssignee: null,
      selectedStatus: null,
      commentText: '',
      commentLoading: false,
      commentError: '',
      showStatusModal: false,
      showAssignModal: false,
    }
  },
  computed: {
    isClosed() {
      return !!(this.ticket && this.ticket.closed_at)
    },
    canEditTicket() {
      return (
        this.user?.role === 'supplier'
        && this.ticket
        && this.ticket.submitter
        && this.ticket.submitter.id === this.user.id
        && this.ticket.status && this.ticket.status.is_default
        && !this.isClosed
      )
    },
    canChangeStatus() {
      return (
        (this.user?.role === 'admin'
         || this.user?.role === 'manager'
         || this.user?.role === 'customer_support'
         || this.user?.role === 'warehouse_operator')
        && !this.isClosed
      )
    },
    canComment() {
      return !!this.user && !this.isClosed
    },
    isAdmin() {
      return this.user?.role === 'admin'
    },
    canViewSupplierOrderInfo() {
      return ['admin', 'manager', 'customer_support', 'warehouse_operator'].includes(this.user?.role)
    },
    filteredStatusList() {
      if (!this.statusList || !this.ticket) return []
      return this.statusList
        .filter(s => s.id !== this.ticket.status.id)
        .map(s => ({ value: s.id, text: s.display_name || s.name }))
    },
    sanitizedDescription() {
      if (!this.ticket || !this.ticket.description) return ''
      return DOMPurify.sanitize(this.ticket.description)
    },
  },
  watch: {
    ticket(newTicket) {
      if (
        this.user?.role === 'supplier'
        && newTicket
        && newTicket.submitter
        && newTicket.submitter.id !== this.user.id
      ) {
        this.$bvToast.toast('Vous n\'avez pas la permission de voir ce ticket.', { variant: 'danger' })
        this.$router.replace({ name: 'SupportTicketList' })
      }
    },
  },
  async mounted() {
    await this.fetchTicket()
    if (this.user.role === 'admin') {
      this.fetchStaffList()
    }
    if (this.canChangeStatus) {
      this.fetchStatusList()
    }
  },
  methods: {
    async confirmDeleteTicket() {
      const message = 'Confirmer la suppression définitive de ce ticket ? Cette action est irréversible.'
      try {
        let ok = true
        if (this.$bvModal && typeof this.$bvModal.msgBoxConfirm === 'function') {
          ok = await this.$bvModal.msgBoxConfirm(message, {
            title: 'Confirmation',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Supprimer',
            cancelTitle: 'Annuler',
            footerClass: 'p-2',
            centered: true,
          })
        } else {
          // eslint-disable-next-line no-alert
          ok = window.confirm(message)
        }
        if (!ok) return
        await axios.delete(`/api/support/tickets/${this.ticket.id}/`)
        if (this.$bvToast) this.$bvToast.toast('Ticket supprimé avec succès.', { variant: 'success' })
        this.$router.replace({ name: 'SupportTicketList' })
      } catch (e) {
        const msg = (e.response && e.response.status === 403)
          ? 'Vous n\'avez pas la permission de supprimer ce ticket.'
          : 'Erreur lors de la suppression du ticket.'
        if (this.$bvToast) this.$bvToast.toast(msg, { variant: 'danger' })
      }
    },
    async fetchTicket() {
      this.ticket = null
      try {
        const { data } = await axios.get(`/api/support/tickets/${this.$route.params.id}/`)
        this.ticket = data
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.$bvToast.toast('Vous n\'avez pas la permission de voir ce ticket.', { variant: 'danger' })
          this.$router.replace({ name: 'SupportTicketList' })
        } else {
          this.$bvToast.toast('Erreur lors du chargement du ticket.', { variant: 'danger' })
        }
      }
    },
    async fetchStaffList() {
      const { data } = await axios.get('/api/auth/staff-members/?role=customer_support')
      const rawList = data.results || data
      this.staffList = rawList.map(user => ({ value: user.id, text: user.username }))
    },
    async fetchStatusList() {
      const { data } = await axios.get('/api/support/statuses/')
      this.statusList = data
    },
    async changeStatusModal() {
      if (!this.selectedStatus) return
      this.statusLoading = true
      try {
        await axios.patch(`/api/support/tickets/${this.ticket.id}/change_status/`, { status: this.selectedStatus })
        this.$bvToast.toast('Statut modifié avec succès.', { variant: 'success' })
        await this.fetchTicket()
        this.showStatusModal = false
      } catch (e) {
        this.$bvToast.toast('Erreur lors du changement de statut.', { variant: 'danger' })
      } finally {
        this.statusLoading = false
      }
    },
    async assignTicketModal() {
      if (!this.selectedAssignee) return
      this.assignLoading = true
      try {
        await axios.patch(`/api/support/tickets/${this.ticket.id}/`, { assignee_id: this.selectedAssignee })
        this.$bvToast.toast('Ticket assigné avec succès.', { variant: 'success' })
        await this.fetchTicket()
        this.showAssignModal = false
      } catch (e) {
        this.$bvToast.toast('Erreur lors de l\'assignation.', { variant: 'danger' })
      } finally {
        this.assignLoading = false
      }
    },
    async addComment() {
      if (!this.commentText.trim()) return
      this.commentLoading = true
      this.commentError = ''
      try {
        await axios.post('/api/support/comments/', {
          ticket: this.ticket.id,
          comment: this.commentText,
        })
        this.commentText = ''
        await this.fetchTicket()
      } catch (e) {
        this.commentError = 'Erreur lors de l\'ajout du commentaire.'
      } finally {
        this.commentLoading = false
      }
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
    formatShortDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
      })
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '-'
      const amount = Number(value)
      if (Number.isNaN(amount)) return `${String(value)} TND`
      return `${amount.toFixed(3)} TND`
    },
    formatPhones(order) {
      const phones = [order.phone1, order.phone2].filter(Boolean)
      return phones.length ? phones.join(' / ') : '-'
    },
    statusVariant(status) {
      if (!status) return 'secondary'
      const map = {
        Ouvert: 'success',
        Fermé: 'danger',
        Résolu: 'primary',
        EnCours: 'warning',
      }
      return map[status.name] || 'secondary'
    },
    getAvatar(user) {
      if (!user) return defaultAvatar
      return defaultAvatar
    },
    copyReference(ref) {
      if (!ref) return
      navigator.clipboard.writeText(ref)
      this.copied = true
      setTimeout(() => { this.copied = false }, 1200)
    },
    getOrderStatusLabel(status) {
      const map = {
        0: 'En Attente',
        1: 'En Pickup',
        2: 'Colis au dépôt',
        3: 'En cours',
        4: 'Livré',
        5: 'Retour au dépôt',
        6: 'Livré payé',
        7: 'Retour expéditeur',
        8: 'En Transfert',
        9: 'Retour payé',
        10: 'Retour inter agence',
        11: 'Retour inter agence reçu',
        12: 'Colis annulé par admin',
        13: 'Colis perdu',
        14: 'À vérifier',
        15: 'En attente de collecte',
        16: 'A rembourser',
        17: 'Remboursé',
      }
      return map[status] !== undefined ? map[status] : status
    },
    getStatusClass(status) {
      if (!status) return 'default'
      const name = status.name ? status.name.toLowerCase() : ''
      if (name.includes('ouvert') || name.includes('open')) return 'open'
      if (name.includes('ferm') || name.includes('clos') || name.includes('closed')) return 'closed'
      if (name.includes('résolu') || name.includes('resolved')) return 'resolved'
      if (name.includes('cours') || name.includes('progress')) return 'progress'
      return 'default'
    },
    goToEdit() {
      this.$router.push({ name: 'SupportTicketEdit', params: { id: this.ticket.id } })
    },
  },
}
</script>

<style scoped>
/* Container */
.ticket-detail-container {
  min-height: 100vh;
  background: #f8fafc;
}

/* Header Section */
.page-header {
  background: linear-gradient(135deg, #090909 0%, #0d4399 100%);
  padding: 1.5rem 2.5rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1600px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.back-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 10px;
  padding: 0.5rem;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.header-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0d4399 0%, #4a90e2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 15px rgba(13, 67, 153, 0.4);
}

.title-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin: 0.25rem 0 0 0;
}

.ref-badge {
  background: linear-gradient(135deg, #0d4399 0%, #4a90e2 100%);
  color: white;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.ref-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(13, 67, 153, 0.4);
}

.status-badge-header {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
}

.status-badge-header.status-open {
  background: linear-gradient(135deg, #090909 0%, #1a202c 100%);
  color: white;
}

.status-badge-header.status-closed {
  background: linear-gradient(135deg, #0d4399 0%, #0d4399 100%);
  color: white;
}

.status-badge-header.status-resolved {
  background: #2FDD6F;
  color: white;
}

.status-badge-header.status-progress {
  background: rgba(91, 166, 227, 0.68);
  color: white;
}

.status-badge-header.status-default {
  background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
  color: white;
}

/* Action Bar */
.action-bar {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 2.5rem;
}

.action-bar-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1600px;
  margin: 0 auto;
}

.ticket-meta {
  display: flex;
  gap: 1.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.9rem;
}

.meta-item.closed {
  color: #ef4444;
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-btn {
  background: linear-gradient(135deg, #090909 0%, #1a202c 100%);
  color: white;
}

.edit-btn:hover {
  box-shadow: 0 4px 12px rgba(9, 9, 9, 0.4);
  transform: translateY(-1px);
}

.status-btn {
  background: linear-gradient(135deg, #090909 0%, #1a202c 100%);
  color: white;
}

.status-btn:hover {
  box-shadow: 0 4px 12px rgba(9, 9, 9, 0.4);
  transform: translateY(-1px);
}

.assign-btn {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  color: white;
}

.assign-btn:hover {
  box-shadow: 0 4px 12px rgba(71, 85, 105, 0.4);
  transform: translateY(-1px);
}

.delete-btn {
  background: linear-gradient(135deg, #0d4399 0%, #0d4399 100%);
  color: white;
}

.delete-btn:hover {
  box-shadow: 0 4px 12px rgba(13, 67, 153, 0.4);
  transform: translateY(-1px);
}

/* Main Content */
.main-content {
  padding: 2rem 2.5rem;
  max-width: 1600px;
  margin: 0 auto;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

/* Info Cards */
.info-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
}

.info-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.card-header-custom {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 600;
  color: #090909;
}

.header-icon-small {
  color: #0d4399;
}

.count-badge {
  background: #0d4399;
  color: white;
  border-radius: 20px;
  padding: 0.2rem 0.6rem;
  font-size: 0.75rem;
  margin-left: auto;
}

.card-body-custom {
  padding: 1.25rem;
}

/* Info Rows */
.info-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 0.95rem;
  color: #1e293b;
  font-weight: 500;
}

.category-badge {
  background: linear-gradient(135deg, #090909 0%, #1a202c 100%);
  color: white;
  border-radius: 6px;
  padding: 0.3rem 0.75rem;
  font-weight: 500;
  display: inline-block;
}

.category-desc {
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(13, 67, 153, 0.03);
  border-left: 3px solid #0d4399;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #64748b;
}

/* Description */
.description-content {
  color: #1e293b;
  line-height: 1.7;
  font-size: 0.95rem;
}

.description-content >>> p {
  margin-bottom: 0.75rem;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #94a3b8;
  gap: 0.5rem;
}

.empty-state.small {
  padding: 1.5rem;
}

.empty-icon {
  opacity: 0.5;
}

/* Orders List */
.orders-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.order-item {
  padding: 1rem;
  background: #f8fafc;
  border-radius: 10px;
  transition: all 0.2s;
}

.order-item:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.order-topline {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.order-main {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: 0;
}

.order-id-badge {
  background: linear-gradient(135deg, #090909 0%, #0d4399 100%);
  color: white;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
  max-width: 12rem;
  overflow-wrap: anywhere;
  text-align: center;
}

.order-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.order-name {
  font-weight: 600;
  color: #1e293b;
  overflow-wrap: anywhere;
}

.order-phone {
  font-size: 0.85rem;
  color: #64748b;
}

.order-details {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
}

.order-price {
  font-weight: 600;
  color: #0d4399;
  white-space: nowrap;
}

.order-status-badge {
  padding: 0.25rem 0.6rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  background: #e2e8f0;
  color: #64748b;
}

.order-info-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 0.85rem;
}

.order-info-block {
  background: #ffffff;
  border: 1px solid #e8eef6;
  border-radius: 8px;
  min-width: 0;
  padding: 0.65rem 0.75rem;
}

.order-info-block.wide {
  grid-column: span 3;
}

.order-info-label {
  color: #64748b;
  display: block;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
}

.order-info-value {
  color: #172033;
  display: block;
  font-size: 0.88rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.pre-line {
  white-space: pre-line;
}

.order-flags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.order-flag {
  background: rgba(100, 116, 139, 0.08);
  border: 1px solid rgba(100, 116, 139, 0.18);
  border-radius: 999px;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.25rem 0.6rem;
}

/* Person Rows */
.person-row {
  padding: 1rem 0;
  border-bottom: 1px solid #f1f5f9;
}

.person-row:last-child {
  border-bottom: none;
}

.person-label {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 0.5rem;
}

.person-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.person-avatar {
  border: 2px solid #0d4399;
}

.person-details {
  display: flex;
  flex-direction: column;
}

.person-name {
  font-weight: 600;
  color: #1e293b;
}

.person-phone {
  font-size: 0.85rem;
  color: #64748b;
}

/* Comments */
.comments-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.comment-item {
  display: flex;
  gap: 0.75rem;
}

.comment-avatar {
  flex-shrink: 0;
  border: 2px solid #e2e8f0;
}

.comment-content {
  flex: 1;
  background: #f8fafc;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  transition: all 0.2s;
}

.comment-content:hover {
  background: rgba(13, 67, 153, 0.03);
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.comment-author {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
}

.comment-date {
  font-size: 0.8rem;
  color: #94a3b8;
}

.comment-text {
  color: #475569;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.5;
}

/* Add Comment Form */
.add-comment-form {
  border-top: 1px solid #e2e8f0;
  padding-top: 1rem;
}

.comment-input {
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.75rem;
  font-size: 0.9rem;
  resize: none;
  transition: all 0.2s;
  width: 100%;
}

.comment-input:focus {
  border-color: #0d4399;
  box-shadow: 0 0 0 4px rgba(13, 67, 153, 0.1);
  outline: none;
}

.add-comment-btn {
  margin-top: 0.75rem;
  background: linear-gradient(135deg, #0d4399 0%, #4a90e2 100%);
  border: none;
  color: white;
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
}

.add-comment-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(13, 67, 153, 0.4);
}

.add-comment-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.comment-error {
  color: #ef4444;
  font-size: 0.85rem;
  margin-top: 0.5rem;
}

/* Timeline */
.timeline {
  position: relative;
  padding-left: 1.5rem;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e2e8f0;
}

.timeline-item {
  position: relative;
  padding-bottom: 1.25rem;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: -1.5rem;
  top: 0.25rem;
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #0d4399 0%, #4a90e2 100%);
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 0 0 2px rgba(13, 67, 153, 0.3);
}

.timeline-content {
  padding-left: 0.5rem;
}

.timeline-message {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
}

.timeline-meta {
  font-size: 0.8rem;
  color: #94a3b8;
  margin-top: 0.25rem;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 1rem;
  color: #64748b;
}

/* Modal Styles */
.modal-form {
  padding: 0.5rem;
}

.custom-select-modal {
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.75rem;
  font-size: 0.95rem;
}

.custom-select-modal:focus {
  border-color: #0d4399;
  box-shadow: 0 0 0 4px rgba(13, 67, 153, 0.1);
}

.modal-confirm-btn {
  width: 100%;
  margin-top: 1rem;
  background: linear-gradient(135deg, #0d4399 0%, #4a90e2 100%);
  border: none;
  color: white;
  padding: 0.75rem;
  border-radius: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-confirm-btn:hover:not(:disabled) {
  box-shadow: 0 4px 15px rgba(13, 67, 153, 0.4);
}

.modal-confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 1rem 1.5rem;
  }

  .header-left {
    flex-wrap: wrap;
  }

  .title-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .page-title {
    font-size: 1.25rem;
  }

  .header-right {
    display: none;
  }

  .action-bar {
    padding: 1rem 1.5rem;
  }

  .action-bar-content {
    flex-direction: column;
    gap: 1rem;
  }

  .ticket-meta {
    flex-wrap: wrap;
  }

  .action-buttons {
    flex-wrap: wrap;
    width: 100%;
  }

  .action-btn {
    flex: 1;
    justify-content: center;
  }

  .main-content {
    padding: 1rem 1.5rem;
  }

  .order-topline {
    flex-direction: column;
  }

  .order-details {
    justify-content: flex-start;
    width: 100%;
  }

  .order-info-grid {
    grid-template-columns: 1fr;
  }

  .order-info-block.wide {
    grid-column: span 1;
  }
}
</style>
